

import React, { useState } from 'react';
import {
  FaTimes, FaPlus, FaTrash, FaQrcode, FaSpinner,
  FaGraduationCap, FaCalendarAlt, FaMapMarkerAlt, FaUsers
} from 'react-icons/fa';
import { evalApi } from '../hooks/useEvalApi';

const CreateSessionModalAvis = ({ onClose, onCreate, isDark, c }) => {
  const [form, setForm] = useState({
    titre: '',
    dates: '',
    lieu: '',
    formateurs: [''],
    participants_attendus: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors,  setErrors]  = useState({});

  const set = (field, val) => {
    setForm(f => ({ ...f, [field]: val }));
    setErrors(e => ({ ...e, [field]: '' }));
  };

  const setFormateur = (i, val) => {
    const arr = [...form.formateurs];
    arr[i] = val;
    setForm(f => ({ ...f, formateurs: arr }));
  };

  const addFormateur    = () => setForm(f => ({ ...f, formateurs: [...f.formateurs, ''] }));
  const removeFormateur = (i) => setForm(f => ({ ...f, formateurs: f.formateurs.filter((_, idx) => idx !== i) }));

  const validate = () => {
    const e = {};
    if (!form.titre.trim())                       e.titre = 'Le titre est requis.';
    if (!form.dates.trim())                       e.dates = 'Les dates sont requises.';
    if (!form.lieu.trim())                        e.lieu  = 'Le lieu est requis.';
    if (!form.participants_attendus || form.participants_attendus < 1)
                                                  e.participants_attendus = 'Nombre de participants requis (min 1).';
    if (form.formateurs.some(f => !f.trim()))     e.formateurs = 'Tous les noms de formateurs doivent être renseignés.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await evalApi.createSession({
        ...form,
        formateurs: form.formateurs.filter(f => f.trim()),
        participants_attendus: parseInt(form.participants_attendus, 10),
      });
      onCreate(res.data); // remonter la session créée au parent
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message || 'Erreur lors de la création.';
      setErrors({ global: msg });
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px',
    border: `1.5px solid ${errors.titre ? '#dc3545' : c.inputB}`,
    borderRadius: 10, background: c.input, color: c.title,
    fontFamily: 'inherit', fontSize: 14, outline: 'none',
    transition: 'border-color 0.2s',
  };

  const labelStyle = {
    fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.6px', color: c.textDim, display: 'block', marginBottom: 7,
  };

  const errStyle = { fontSize: 12, color: '#dc3545', marginTop: 4, fontWeight: 600 };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: isDark ? 'rgba(9,12,22,0.85)' : 'rgba(15,23,42,0.55)',
        backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 3000, padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: c.modalBg, border: `1px solid ${c.cardB}`,
          borderRadius: 22, width: '100%', maxWidth: 560,
          maxHeight: '92vh', overflowY: 'auto',
          boxShadow: '0 28px 72px rgba(0,0,0,0.24)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 22px', borderBottom: `1px solid ${c.divider}`,
          position: 'sticky', top: 0, background: c.modalBg,
          borderRadius: '22px 22px 0 0', zIndex: 1,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 11,
              background: 'rgba(253,126,20,0.12)', color: '#fd7e14',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
            }}>
              <FaQrcode />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: c.title }}>Nouvelle session d'évaluation</div>
              <div style={{ fontSize: 12, color: c.textDim }}>Génère un QR code unique pour les participants</div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 34, height: 34, borderRadius: 9,
              border: `1.5px solid ${c.cardB}`, background: 'transparent',
              color: c.textDim, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
            }}
          >
            <FaTimes />
          </button>
        </div>

        <div style={{ padding: '24px 26px', display: 'flex', flexDirection: 'column', gap: 18 }}>

          {errors.global && (
            <div style={{ padding: '12px 16px', background: 'rgba(220,53,69,0.08)', border: '1px solid rgba(220,53,69,0.25)', borderRadius: 10, fontSize: 13.5, color: '#dc3545', fontWeight: 600 }}>
              {errors.global}
            </div>
          )}

          {/* Titre */}
          <div>
            <label style={labelStyle}><FaGraduationCap style={{ marginRight: 6 }} />Titre de la formation</label>
            <input
              style={{ ...inputStyle, borderColor: errors.titre ? '#dc3545' : c.inputB }}
              placeholder="Ex: Management et Leadership Stratégique"
              value={form.titre}
              onChange={e => set('titre', e.target.value)}
            />
            {errors.titre && <div style={errStyle}>{errors.titre}</div>}
          </div>

          {/* Dates */}
          <div>
            <label style={labelStyle}><FaCalendarAlt style={{ marginRight: 6 }} />Dates de la formation</label>
            <input
              style={{ ...inputStyle, borderColor: errors.dates ? '#dc3545' : c.inputB }}
              placeholder="Ex: 15 – 19 Déc 2025"
              value={form.dates}
              onChange={e => set('dates', e.target.value)}
            />
            {errors.dates && <div style={errStyle}>{errors.dates}</div>}
          </div>

          {/* Lieu */}
          <div>
            <label style={labelStyle}><FaMapMarkerAlt style={{ marginRight: 6 }} />Lieu</label>
            <input
              style={{ ...inputStyle, borderColor: errors.lieu ? '#dc3545' : c.inputB }}
              placeholder="Ex: Hôtel du Lac / Cotonou, Bénin"
              value={form.lieu}
              onChange={e => set('lieu', e.target.value)}
            />
            {errors.lieu && <div style={errStyle}>{errors.lieu}</div>}
          </div>

          {/* Nombre de participants */}
          <div>
            <label style={labelStyle}><FaUsers style={{ marginRight: 6 }} />Nombre de participants attendus</label>
            <input
              type="number" min="1" max="500"
              style={{ ...inputStyle, borderColor: errors.participants_attendus ? '#dc3545' : c.inputB }}
              placeholder="Ex: 15"
              value={form.participants_attendus}
              onChange={e => set('participants_attendus', e.target.value)}
            />
            {errors.participants_attendus && <div style={errStyle}>{errors.participants_attendus}</div>}
            <div style={{ fontSize: 12, color: c.textDim, marginTop: 5 }}>
              Le formulaire se fermera automatiquement une fois ce quota atteint ou après 1h.
            </div>
          </div>

          {/* Formateurs */}
          <div>
            <label style={labelStyle}><FaGraduationCap style={{ marginRight: 6 }} />Formateur(s)</label>
            {form.formateurs.map((f, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  style={{ ...inputStyle, borderColor: errors.formateurs ? '#dc3545' : c.inputB }}
                  placeholder={`Nom du formateur ${i + 1}`}
                  value={f}
                  onChange={e => setFormateur(i, e.target.value)}
                />
                {form.formateurs.length > 1 && (
                  <button
                    onClick={() => removeFormateur(i)}
                    style={{
                      padding: '0 14px', borderRadius: 10,
                      border: `1.5px solid rgba(220,53,69,0.3)`,
                      background: 'rgba(220,53,69,0.06)', color: '#dc3545',
                      cursor: 'pointer', flexShrink: 0, fontSize: 13,
                    }}
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            ))}
            {errors.formateurs && <div style={errStyle}>{errors.formateurs}</div>}
            <button
              onClick={addFormateur}
              style={{
                display: 'flex', alignItems: 'center', gap: 7,
                padding: '9px 14px', borderRadius: 9,
                border: `1.5px dashed ${c.cardB}`, background: 'transparent',
                color: c.textDim, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                fontFamily: 'inherit', marginTop: 4,
              }}
            >
              <FaPlus style={{ fontSize: 11 }} /> Ajouter un formateur
            </button>
          </div>

          {/* Info box */}
          <div style={{
            padding: '13px 16px', borderRadius: 11,
            background: isDark ? 'rgba(253,126,20,0.06)' : 'rgba(253,126,20,0.07)',
            border: '1px solid rgba(253,126,20,0.25)',
            fontSize: 13, color: c.text, lineHeight: 1.6,
          }}>
            📋 <strong>Comment ça fonctionne :</strong> Après création, activez le QR code depuis le panneau. Le compte à rebours de <strong>1h démarre à la première ouverture</strong> du lien par un participant.
          </div>

          {/* Boutons */}
          <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
            <button
              onClick={onClose}
              style={{
                flex: 1, padding: '14px', borderRadius: 12,
                border: `1.5px solid ${c.cardB}`, background: 'transparent',
                color: c.textDim, fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                flex: 2, padding: '14px', borderRadius: 12, border: 'none',
                background: loading ? '#adb5bd' : 'linear-gradient(135deg,#fd7e14,#e65c00)',
                color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
                boxShadow: loading ? 'none' : '0 8px 24px rgba(253,126,20,0.28)',
              }}
            >
              {loading
                ? <><FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> Création…</>
                : <><FaQrcode /> Créer & générer le QR code</>
              }
            </button>
          </div>
        </div>
        <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
      </div>
    </div>
  );
};

export default CreateSessionModalAvis;